import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageDropdown from 'components/navbar/top/MessageDropdown';
import NotificationDropdown from 'components/navbar/top/NotificationDropdown';
import SalesNotificationDropdown from 'components/navbar/top/SalesNotificationDropdown';
import ProfileDropdown from 'components/navbar/top/ProfileDropdown';
import AppContext from 'context/Context';
import React, { useContext, useEffect, useState } from 'react';
import { Dropdown, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import NineDotMenu from './NineDotMenu';
import { WORKSPACE_MODES } from 'helpers/workspace';
import { TOUR_START_EVENT } from 'components/tour/AppJoyride';
import { findHelpModuleByPath, hasAccessToHelpModule } from 'components/help/helpRegistry';
import { Link, useLocation } from 'react-router-dom';

const TopNavRightSideNavItem = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { hasAnyPermission, getCurrentWorkspaceMode, hasPermission } = auth;
  const { pathname } = useLocation();
  const {
    config: { isDark, isRTL },
    setConfig
  } = useContext(AppContext);
  const canViewNotifications = hasAnyPermission([
    'notifications.view_internal',
    'notifications.view_mercadolibre',
    'notifications.view_questions'
  ]);
  const canViewMessages = hasAnyPermission([
    'messages.view_internal',
    'messages.view_mercadolibre'
  ]);
  const isSalesMode = getCurrentWorkspaceMode() === WORKSPACE_MODES.SALES;
  const canViewSalesNotifications = hasPermission('crm.view');
  const currentHelpModuleCandidate = findHelpModuleByPath(pathname);
  const currentHelpModule = currentHelpModuleCandidate && hasAccessToHelpModule(currentHelpModuleCandidate, auth)
    ? currentHelpModuleCandidate
    : null;
  const currentHelpPath = currentHelpModule ? `/help?module=${currentHelpModule.key}` : '/help';
  const hasCurrentModuleTour = Boolean(currentHelpModule?.tourSteps?.length);
  const [isModuleTourReady, setIsModuleTourReady] = useState(false);

  useEffect(() => {
    if (!hasCurrentModuleTour) {
      setIsModuleTourReady(false);
      return undefined;
    }

    const readySelector = currentHelpModule?.tourReadySelector;
    if (!readySelector) {
      setIsModuleTourReady(true);
      return undefined;
    }

    const updateReadyState = () => {
      setIsModuleTourReady(Boolean(document.querySelector(readySelector)));
    };

    updateReadyState();
    const observer = new MutationObserver(updateReadyState);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [currentHelpModule, hasCurrentModuleTour, pathname]);

  const handleStartTour = tourKey => {
    window.dispatchEvent(
      new CustomEvent(TOUR_START_EVENT, {
        detail: { tourKey, moduleKey: currentHelpModule?.key || null }
      })
    );
  };

  return (
    <Nav
      navbar
      className="navbar-nav-icons ms-auto flex-row align-items-center"
      as="ul"
      data-tour="top-actions"
    >
      <Dropdown navbar as="li" align="end">
        <Dropdown.Toggle
          bsPrefix="toggle"
          className="nav-link px-2 d-flex align-items-center"
        >
          <FontAwesomeIcon icon="question-circle" className="fs-0" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end py-2">
          <Dropdown.Item onClick={() => handleStartTour('app')}>
            <FontAwesomeIcon icon="route" className="me-2 text-600" />
            {t('HELP_CENTER.ACTIONS.START_APP_TOUR')}
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!hasCurrentModuleTour || !isModuleTourReady}
            onClick={() => handleStartTour('module')}
          >
            <FontAwesomeIcon icon="play" className="me-2 text-600" />
            {t('HELP_CENTER.ACTIONS.START_MODULE_TOUR')}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            as={Link}
            to={currentHelpPath}
          >
            <FontAwesomeIcon icon="book-open" className="me-2 text-600" />
            {t('HELP_CENTER.ACTIONS.OPEN_HELP')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Nav.Item as={'li'}>
        <Nav.Link
          className="px-2 theme-control-toggle"
          onClick={() => setConfig('isDark', !isDark)}
        >
          <OverlayTrigger
            key="left"
            placement={isRTL ? 'bottom' : 'left'}
            overlay={
              <Tooltip style={{ position: 'fixed' }} id="ThemeColor">
                {isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              </Tooltip>
            }
          >
            <div className="theme-control-toggle-label">
              <FontAwesomeIcon
                icon={isDark ? 'sun' : 'moon'}
                className="fs-0"
              />
            </div>
          </OverlayTrigger>
        </Nav.Link>
      </Nav.Item>

      {canViewMessages ? <MessageDropdown /> : null}
      {isSalesMode
        ? (canViewSalesNotifications ? <SalesNotificationDropdown /> : null)
        : (canViewNotifications ? <NotificationDropdown /> : null)}
      <NineDotMenu />
      <ProfileDropdown />
    </Nav>
  );
};

export default TopNavRightSideNavItem;
